// src/components/TipoEnvase/TipoEnvaseIndex.tsx

import React, { useState, useEffect, useRef } from "react";
import * as S from "../Envases/TipoEnvase.styles";

// Importaciones de DevExtreme
import DataGrid, {
  Column,
  HeaderFilter,
  Paging,
  Toolbar,
  Item,
  Pager,
  ColumnFixing,
  SearchPanel,
  FilterRow,
} from "devextreme-react/data-grid";
import { Popup } from "devextreme-react/popup";
import Form, { SimpleItem, Label, RequiredRule } from "devextreme-react/form";
import type { default as FormInstance } from "devextreme/ui/form";
import { Button } from "devextreme-react/button";
import { ButtonGroup } from "devextreme-react/button-group";
import notify from "devextreme/ui/notify";

// --- CAMBIO 1: Importamos el nuevo icono para 'Activar' ---
import { FiEdit, FiTrash2, FiCheckCircle } from "react-icons/fi";
import { apiService } from "../../services/apiService";
import { ApiError } from "../Sucursales/SucursalesForm";
import axios, { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { ExportButton } from "../AcetilenoData/Acetileno.styles";
import { exportRowsToExcel } from "../../helpers/exportExcel";

// --- Tipos y Datos ---
interface TipoEnvase {
  id: string;
  numSucursal: number;
  nombreSuc: string;
  um: string;
  capacidad: number;
  estatusActivo: boolean;
}

// Opciones para el componente ButtonGroup
const estatusOptions = [
  { text: "Inactivo", value: false },
  { text: "Activo", value: true },
];

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    const errorMessage =
      error.response?.data?.message || "Ocurrió un error en el servidor.";
    return {
      message: errorMessage,
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return {
    message: "Ocurrió un error inesperado. Revisa tu conexión de red.",
  };
};

const SucursalesIndex = () => {
  const [dataSource, setDataSource] = useState<TipoEnvase[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormPopupVisible, setIsFormPopupVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<Partial<TipoEnvase> | null>(
    null
  );
  const navigate = useNavigate(); // ✅ Hook para regresar
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<TipoEnvase | null>(null);

  const formRef = useRef<FormInstance | null>(null);

  const fetchTiposEnvase = async () => {
    setLoading(true);

    try {
      const response = await apiService.get<TipoEnvase[]>(
        "/api/Catalogos/Sucursales"
      );
      setDataSource(response);
      return { data: response, error: null };
    } catch (err: any) {
      return { data: null, error: handleApiError(err) };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTiposEnvase();
  }, []);

  const handleAdd = () => {
    setEditingItem({ estatusActivo: true }); // Por defecto, los nuevos registros están activos
    setIsFormPopupVisible(true);
  };

  const handleEdit = (data: TipoEnvase) => {
    setEditingItem({ ...data });
    setIsFormPopupVisible(true);
  };

  const handleDelete = (data: TipoEnvase) => {
    setItemToDelete(data);
    setIsDeletePopupVisible(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        await apiService.patch(
          `/api/Catalogos/sucursales/${itemToDelete.id}/cambiastatus`,
          ""
        );
        fetchTiposEnvase();
        notify("Cambio de estatus realizado con éxito.", "success", 2000);
      } catch (error) {
        console.error("Error al cambiar estatus:", error);
        notify("Error al cambiar el estatus del registro.", "error", 3000);
      } finally {
        setIsDeletePopupVisible(false);
        setItemToDelete(null);
      }
    }
  };

  // --- FUNCIÓN DE GUARDADO CON LÓGICA DE ACTUALIZACIÓN CLARA ---
  const handleSave = async () => {
    const form = formRef.current;
    if (!form) return;

    const validation = await form.validate();
    if (!validation.isValid) {
      notify(
        "Por favor, complete todos los campos requeridos.",
        "warning",
        2000
      );
      return;
    }

    const formData = form.option("formData") as TipoEnvase;

    try {
      if (formData.id) {
        // --- ACTUALIZAR (PUT) ---
        const payload = {
          ...formData, // Esto asegura que numSucursal y nombreSuc siempre se envíen
          usuarioDelUltimoCambio: "Sistema",
          fechaUltimoCambio: new Date().toISOString(),
        };

        const response = await apiService.put<TipoEnvase, TipoEnvase>(
          `/api/Catalogos/sucursales/${formData.id}`,
          payload
        );

        setDataSource((prev) =>
          prev.map((item) => (item.id === formData.id ? response : item))
        );
        notify("Sucursal actualizada con éxito.", "success", 2000);
      } else {
        // --- CREAR (POST) ---
        const payload = {
          ...formData,
          numSucursal: formData.numSucursal,
          nombreSuc: formData.nombreSuc,
        };
        const response = await apiService.post<TipoEnvase, TipoEnvase>(
          "/api/Catalogos/Sucursales",
          payload
        );
        setDataSource((prev) => [...prev, response]);
        notify("Sucursal creada con éxito.", "success", 2000);
      }
      setIsFormPopupVisible(false);
    } catch (error) {
      console.error("Error al guardar:", error);
      notify("Error al guardar el registro.", "error", 3000);
    }
  };

  const handleExportExcel = async () => {
    if (!dataSource.length) {
      notify({
        message: "No hay datos para exportar.",
        type: "warning",
        displayTime: 3000,
      });
      return;
    }

    try {
      await exportRowsToExcel(
        "Catalogo_Sucursales.xlsx",
        "Sucursales",
        [
          { header: "CC", key: "numSucursal", width: 12 },
          { header: "Nombre sucursal", key: "nombreSuc", width: 30 },
          {
            header: "Estatus",
            key: "estatusActivo",
            width: 14,
            format: (row) => (row.estatusActivo ? "Activo" : "Inactivo"),
          },
        ],
        dataSource
      );
    } catch (error: any) {
      notify({
        message: error?.message || "Error al exportar.",
        type: "error",
        displayTime: 3000,
      });
    }
  };

  const EstatusCell = (cellInfo: any) => {
    const isActive = cellInfo.value === true;
    return (
      <div
        style={{
          padding: "4px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 500,
          color: "#fff",
          backgroundColor: isActive ? "#16a34a" : "#71717a",
          textAlign: "center",
        }}
      >
        {isActive ? "Activo" : "Inactivo"}
      </div>
    );
  };

  // --- CAMBIO 1: Lógica condicional en los botones de acción ---
  const ActionsCell = (cellData: any) => {
    const sucursal = cellData.data as TipoEnvase;
    const isActive = sucursal.estatusActivo;

    return (
      <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
        <Button
          hint="Editar"
          onClick={() => handleEdit(sucursal)}
          stylingMode="text"
        >
          <FiEdit size={18} color="#1d4ed8" />
        </Button>

        <Button
          hint={isActive ? "Desactivar" : "Activar"}
          onClick={() => handleDelete(sucursal)}
          stylingMode="text"
        >
          {isActive ? (
            <FiTrash2 size={18} color="#dc2626" />
          ) : (
            <FiCheckCircle size={18} color="#16a34a" />
          )}
        </Button>
      </div>
    );
  };

  return (
    <S.PageWrapper>
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}
      >
        <Button
          icon="chevronleft"
          text="Regresar"
          stylingMode="outlined"
          type="default"
          onClick={() => navigate(-1)} 
        />
      </div>
      <S.Header>Catálogo de sucursales</S.Header>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "12px",
        }}
      >
        <ExportButton
          type="button"
          $active={dataSource.length > 0}
          disabled={!dataSource.length}
          onClick={handleExportExcel}
          title={
            dataSource.length
              ? "Exportar a Excel"
              : "No hay datos para exportar"
          }
        >
          Exportar
        </ExportButton>
      </div>
      <DataGrid
        dataSource={dataSource}
        keyExpr="id"
        showBorders={true}
        columnAutoWidth={true}
        hoverStateEnabled={true}
        loadPanel={{ enabled: loading }}
      >
        <HeaderFilter visible={true} />
        <FilterRow visible={true} />
        
        <Toolbar>
          <Item name="searchPanel" />
          <Item
            location="after"
            widget="dxButton"
            options={{
              text: "Añadir sucursal",
              icon: "add",
              stylingMode: "contained",
              type: "default",
              onClick: handleAdd,
            }}
          />
        </Toolbar>
        <SearchPanel visible={true} highlightCaseSensitive={true} placeholder="Buscar..." />
        <ColumnFixing enabled={true} />
        <Column dataField="numSucursal" caption="CC" alignment="left" width={200}/>
        <Column dataField="nombreSuc" caption="Nombre sucursal" width={250}/>
        <Column
          dataField="estatusActivo"
          caption="Estatus"
          alignment="center"
          cellRender={EstatusCell}
          width={300}
        />
        <Column
          caption="Acciones"
          alignment="center"
          width={300}
          cellRender={ActionsCell}
        />
      </DataGrid>
      <Popup
        visible={isFormPopupVisible}
        onHiding={() => setIsFormPopupVisible(false)}
        dragEnabled={false}
        showCloseButton={true}
        showTitle={true}
        title={
          editingItem && editingItem.id ? "Editar sucursal" : "Añadir sucursal"
        }
        width={500}
        height="auto"
        toolbarItems={[
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Guardar",
              type: "success",
              stylingMode: "contained",
              onClick: handleSave,
            },
          },
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Cancelar",
              type: "default",
              stylingMode: "text",
              onClick: () => setIsFormPopupVisible(false),
            },
          },
        ]}
      >
        <S.FormContainer>
          <Form
            formData={editingItem}
            onInitialized={(e) => {
              formRef.current = e.component ?? null;
            }}
          >
            <SimpleItem dataField="numSucursal">
              <Label text="Núm. Sucursal" />
              <RequiredRule message="El Núm. Sucursal es requerido." />
            </SimpleItem>
            <SimpleItem dataField="nombreSuc">
              <Label text="Nombre sucursal" />
              <RequiredRule message="El nombre de la sucursal es requerido." />
            </SimpleItem>

            {/* --- CAMBIO 2: Oculta este campo del formulario --- */}
            <SimpleItem
              dataField="estatusActivo"
              visible={false}
              render={() => (
                <ButtonGroup
                  items={estatusOptions}
                  keyExpr="value"
                  stylingMode="contained"
                  selectedItemKeys={
                    editingItem?.estatusActivo !== undefined
                      ? [editingItem.estatusActivo]
                      : []
                  }
                  onSelectionChanged={(e) => {
                    if (e.addedItems.length > 0) {
                      setEditingItem((prev) => ({
                        ...prev,
                        estatusActivo: e.addedItems[0].value,
                      }));
                      formRef.current?.updateData(
                        "estatusActivo",
                        e.addedItems[0].value
                      );
                    }
                  }}
                />
              )}
            >
              <Label text="Estatus" />
            </SimpleItem>
          </Form>
        </S.FormContainer>
      </Popup>

      {/* --- CAMBIO 1.1: Popup de Confirmación con mensajes dinámicos --- */}
      <Popup
        visible={isDeletePopupVisible}
        onHiding={() => setIsDeletePopupVisible(false)}
        dragEnabled={false}
        showTitle={true}
        title={`Confirmar ${
          itemToDelete?.estatusActivo ? "Desactivación" : "Activación"
        }`}
        width={400}
        height="auto"
        toolbarItems={[
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: `Sí, ${
                itemToDelete?.estatusActivo ? "Desactivar" : "Activar"
              }`,
              type: itemToDelete?.estatusActivo ? "danger" : "success",
              stylingMode: "contained",
              onClick: confirmDelete,
            },
          },
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Cancelar",
              stylingMode: "text",
              onClick: () => setIsDeletePopupVisible(false),
            },
          },
        ]}
      >
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>
            ¿Estás seguro de que deseas{" "}
            <strong>
              {itemToDelete?.estatusActivo ? "desactivar" : "activar"}
            </strong>{" "}
            la sucursal
          </p>
          <p style={{ fontWeight: "bold", margin: "10px 0" }}>
            "{itemToDelete?.nombreSuc}"?
          </p>
        </div>
      </Popup>
    </S.PageWrapper>
  );
};

export default SucursalesIndex;
